import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import '@google/model-viewer';
import { Camera, Box, MessageCircle, Copy, Check, X, ImagePlus, RotateCcw, Info } from 'lucide-react';
import { startAR, isWebXRARSupported } from './arSession';

const TAMANHOS = [
  { id: '3x2', chip: '3 × 2 m', label: '3,00 × 2,00 m', desc: 'largura × altura', aspect: 1.5, W: 3.0, H: 2.0 },
  { id: '4x3', chip: '4 × 3 m', label: '4,00 × 3,00 m', desc: 'largura × altura', aspect: 4 / 3, W: 4.0, H: 3.0 },
  { id: '4x25', chip: '4 × 2,5 m', label: '4,00 × 2,50 m', desc: 'largura × altura', aspect: 1.6, W: 4.0, H: 2.5 },
];

const isInstagram = () => /Instagram|FBAN|FBAV/i.test(navigator.userAgent);

const cropCover = (img, aspect) => {
  const W = 1600, H = Math.round(1600 / aspect);
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const ctx = c.getContext('2d');
  const ia = img.width / img.height;
  let sx = 0, sy = 0, sw = img.width, sh = img.height;
  if (ia > aspect) { sw = img.height * aspect; sx = (img.width - sw) / 2; }
  else { sh = img.width / aspect; sy = (img.height - sh) / 2; }
  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, W, H);
  return c.toDataURL('image/jpeg', 0.9);
};

export default function ExperienciaPage() {
  const mvRef = useRef(null);
  const fileRef = useRef(null);
  const customImgRef = useRef(null);
  const [tamanho, setTamanho] = useState('3x2');
  const [conteudo, setConteudo] = useState('padrao');
  const [arStatus, setArStatus] = useState(null);
  const [showInApp, setShowInApp] = useState(false);
  const [copied, setCopied] = useState(false);
  const [webxr, setWebxr] = useState(false);

  useEffect(() => {
    document.title = 'Experiência AR | Grupo Tomaselli';
    isWebXRARSupported().then(setWebxr);
  }, []);

  const t = TAMANHOS.find((x) => x.id === tamanho);

  const applyScreenTexture = useCallback(async (url) => {
    const mv = mvRef.current;
    if (!mv || !mv.model) return;
    try {
      const tex = await mv.createTexture(url);
      mv.model.materials.forEach((m) => {
        if (m.name === 'ScreenMat') {
          m.pbrMetallicRoughness.baseColorTexture?.setTexture(tex);
          m.emissiveTexture?.setTexture(tex);
        }
      });
    } catch (e) { console.error('texture', e); }
  }, []);

  useEffect(() => {
    const mv = mvRef.current;
    if (!mv) return;
    const onLoad = () => {
      if (customImgRef.current && conteudo === 'propria') {
        applyScreenTexture(cropCover(customImgRef.current, TAMANHOS.find((x) => x.id === tamanho).aspect));
      }
    };
    mv.addEventListener('load', onLoad);
    return () => mv.removeEventListener('load', onLoad);
  }, [tamanho, conteudo, applyScreenTexture]);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const url = URL.createObjectURL(f);
    const img = new Image();
    img.onload = () => {
      customImgRef.current = img;
      setConteudo('propria');
      applyScreenTexture(cropCover(img, t.aspect));
      URL.revokeObjectURL(url);
    };
    img.src = url;
    e.target.value = '';
  };

  const voltarPadrao = () => {
    customImgRef.current = null;
    setConteudo('padrao');
    applyScreenTexture(`/ar/tela-${tamanho}.jpg`);
  };

  const verNoEspaco = async () => {
    if (isInstagram()) { setShowInApp(true); return; }
    setArStatus(null);
    try {
      await startAR({
        sizes: TAMANHOS.map((s) => ({ ...s, glbUrl: `/ar/painel-${s.id}.glb`, texUrl: `/ar/tela-${s.id}.jpg` })),
        initialId: tamanho,
        customTexFor: (s) => (customImgRef.current && conteudo === 'propria' ? cropCover(customImgRef.current, s.W / s.H) : null),
      });
    } catch (e) {
      console.error('ar', e);
      setArStatus('unsupported');
    }
  };

  const copiarLink = () => {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(window.location.href).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2500); });
    }
  };

  const waText = encodeURIComponent(`Olá! Visualizei na experiência o painel de LED ${t.label} e gostaria de solicitar um orçamento.`);

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="experiencia-page">
      <header className="py-3 px-5 border-b border-slate-100 flex items-center justify-between">
        <Link to="/" data-testid="exp-logo-link">
          <img src="/promo/logo40v2-final-semtag.png" alt="Grupo Tomaselli" className="h-10 sm:h-12" />
        </Link>
        <span style={{ fontFamily: "'Poppins', sans-serif" }} className="text-[10px] font-bold tracking-[0.14em] uppercase text-[#00A8E8]">
          Experiência AR
        </span>
      </header>

      <main className="flex-1 max-w-xl w-full mx-auto px-5 py-6 flex flex-col gap-5">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#0A2463] leading-tight" data-testid="exp-headline">
            Veja como um painel de LED ficaria no seu espaço.
          </h1>
          <p className="text-[#475569] text-sm mt-2">
            Aponte a câmera para o local onde gostaria de instalar o painel e visualize uma simulação em tamanho real.
          </p>
        </div>

        <div className="rounded-2xl overflow-hidden border border-slate-200 bg-gradient-to-b from-slate-50 to-slate-100" data-testid="exp-viewer">
          <model-viewer
            ref={mvRef}
            src={`/ar/painel-${tamanho}.glb`}
            alt={`Painel de LED ${t.label}`}
            camera-controls
            shadow-intensity="1"
            camera-orbit="6deg 86deg 80%"
            min-camera-orbit="auto auto 50%"
            max-camera-orbit="auto auto 180%"
            style={{ width: '100%', height: '320px', backgroundColor: 'transparent' }}
          >
            <div slot="progress-bar" />
          </model-viewer>
          <p className="text-center text-[11px] text-[#94A3B8] pb-2">Gire e aproxime com os dedos · prévia 3D</p>
        </div>

        <div>
          <p style={{ fontFamily: "'Poppins', sans-serif" }} className="text-[11px] font-bold tracking-[0.12em] uppercase text-[#0A2463] mb-2">
            Conteúdo da tela
          </p>
          <div className="grid grid-cols-2 gap-2">
            <button onClick={voltarPadrao} data-testid="conteudo-padrao-btn"
              className={`rounded-xl border-2 py-3 px-2 flex items-center justify-center gap-2 text-sm font-bold transition-all ${
                conteudo === 'padrao' ? 'border-[#00A8E8] bg-[#00A8E8]/10 text-[#0A2463]' : 'border-slate-200 text-[#475569] hover:border-slate-300'
              }`}>
              Imagem padrão
            </button>
            <button onClick={() => fileRef.current?.click()} data-testid="usar-minha-imagem-btn"
              className={`rounded-xl border-2 py-3 px-2 flex items-center justify-center gap-2 text-sm font-bold transition-all ${
                conteudo === 'propria' ? 'border-[#00A8E8] bg-[#00A8E8]/10 text-[#0A2463]' : 'border-slate-200 text-[#475569] hover:border-slate-300'
              }`}>
              <ImagePlus size={16} />
              Usar minha imagem
            </button>
          </div>
          <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp" className="hidden" onChange={onFile} data-testid="file-input" />
          {conteudo === 'propria' && (
            <div className="mt-2 flex items-center justify-between gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2" data-testid="propria-info">
              <p className="text-[11px] text-[#475569] flex items-start gap-1.5">
                <Info size={13} className="flex-shrink-0 mt-0.5 text-[#00A8E8]" />
                {webxr
                  ? 'Sua imagem é processada só no seu aparelho e aparece na prévia e no modo AR.'
                  : 'Sua imagem é processada só no seu aparelho e aparece na prévia e na visualização com câmera.'}
              </p>
              <button onClick={voltarPadrao} data-testid="voltar-padrao-btn"
                className="flex-shrink-0 flex items-center gap-1 text-[11px] font-bold text-[#0A2463] underline">
                <RotateCcw size={11} /> Imagem padrão
              </button>
            </div>
          )}
        </div>

        <div>
          <p style={{ fontFamily: "'Poppins', sans-serif" }} className="text-[11px] font-bold tracking-[0.12em] uppercase text-[#0A2463] mb-2">
            Escolha o tamanho da tela
          </p>
          <div className="grid grid-cols-3 gap-2">
            {TAMANHOS.map((tm) => (
              <button key={tm.id} onClick={() => setTamanho(tm.id)} data-testid={`tamanho-${tm.id}`}
                className={`rounded-xl border-2 py-3 px-1 text-center transition-all ${
                  tamanho === tm.id ? 'border-[#00A8E8] bg-[#00A8E8]/10 text-[#0A2463]' : 'border-slate-200 text-[#475569] hover:border-slate-300'
                }`}>
                <span className="block font-bold text-sm sm:text-base whitespace-nowrap">{tm.label}</span>
                <span className="block text-[10px] text-[#94A3B8]">{tm.desc}</span>
              </button>
            ))}
          </div>
        </div>

        <button onClick={verNoEspaco} data-testid="ver-no-espaco-btn"
          className="w-full flex items-center justify-center gap-2.5 bg-[#0A2463] hover:bg-[#103487] text-white py-4 rounded-full text-base font-bold transition-all shadow-lg shadow-blue-900/20">
          <Camera size={20} />
          Ver no meu espaço
        </button>

        {arStatus === 'unsupported' && (
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center" data-testid="ar-unsupported">
            <p className="text-[#475569] text-sm">Seu dispositivo não suporta esta experiência em realidade aumentada neste navegador.</p>
            <button onClick={() => { setArStatus(null); mvRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }); }}
              data-testid="ver-3d-btn"
              className="mt-3 inline-flex items-center gap-2 border-2 border-[#0A2463] text-[#0A2463] px-6 py-2.5 rounded-full text-sm font-bold">
              <Box size={16} /> Visualizar modelo 3D
            </button>
          </div>
        )}

        <div className="border-t border-slate-100 pt-5 text-center">
          <p className="text-[#0A2463] font-bold text-lg mb-3">Gostou de como ficou?</p>
          <a href={`https://wa.me/5592988020092?text=${waText}`} target="_blank" rel="noopener noreferrer" data-testid="solicitar-orcamento-btn"
            className="w-full flex items-center justify-center gap-2.5 bg-[#25D366] hover:bg-[#1EBE5A] text-white py-4 rounded-full text-base font-bold transition-all shadow-lg shadow-green-500/20">
            <MessageCircle size={20} />
            Solicitar orçamento
          </a>
          <p className="text-[11px] text-[#94A3B8] mt-4">
            Visualização ilustrativa. Dimensões e condições de instalação devem ser confirmadas pela equipe Tomaselli.
          </p>
        </div>
      </main>

      {showInApp && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-end sm:items-center justify-center p-4" data-testid="inapp-modal">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm relative">
            <button onClick={() => setShowInApp(false)} className="absolute top-3 right-3 text-[#94A3B8]" data-testid="inapp-close">
              <X size={20} />
            </button>
            <p className="text-[#0A2463] font-bold mb-2">Abra no navegador do celular</p>
            <p className="text-[#475569] text-sm mb-4">
              Para usar a câmera e visualizar o painel no seu espaço, abra esta página no Safari ou Chrome.
              Toque nos três pontos (⋯) no canto da tela e escolha "Abrir no navegador".
            </p>
            <button onClick={copiarLink} data-testid="copiar-link-btn"
              className="w-full flex items-center justify-center gap-2 border-2 border-[#0A2463] text-[#0A2463] py-3 rounded-full text-sm font-bold">
              {copied ? <Check size={16} className="text-green-500" /> : <Copy size={16} />}
              {copied ? 'Link copiado!' : 'Copiar link da experiência'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
